'use client';

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react';
import { usePathname } from 'next/navigation';
import { useAuthSession } from '@/components/rbac/AuthSessionProvider';
import { useIsMobile } from '@/hooks/useIsMobile';
import { marcarTutorialCompletado } from '@/lib/tutoriales';
import { TUTORIAL_STEPS, tutorialForPath, type TutorialClave } from './tutorialSteps';
import { SpotlightTour } from './SpotlightTour';

/** Runtime data a launcher can pass into a tour (e.g. the name for a preview mockup). */
interface LaunchOptions {
	previewName?: string;
}

interface TutorialContextValue {
	/** Launch a tutorial on demand (used by the help button). */
	launch: (clave: TutorialClave, opts?: LaunchOptions) => void;
	/** The tutorial that applies to the current route, or null. */
	tutorialForCurrentPath: TutorialClave | null;
}

const TutorialContext = createContext<TutorialContextValue | undefined>(undefined);

// 'auto' = shown by the system on first visit (persists on finish/skip; shows the
// "no volver a mostrar" checkbox). 'manual' = launched from the help button (one
// time; never persists; no checkbox).
type ActiveTour = { clave: TutorialClave; mode: 'auto' | 'manual'; previewName?: string };

/**
 * Orchestrates the spotlight tours (R34):
 * - Logged-in users auto-see the `home` tour on `/` until they complete it
 *   (progress in DB via /auth/me → /tutoriales/me). No localStorage.
 * - Anyone can relaunch the current page's tour from the help button (manual).
 * - Auto tours persist when the user finishes (with the checkbox) OR skips.
 * - Manual tours never persist and never show the checkbox.
 */
export function TutorialProvider({ children }: { children: ReactNode }) {
	const { user, status } = useAuthSession();
	const pathname = usePathname();
	const [active, setActive] = useState<ActiveTour | null>(null);
	// Tutorials marked completed during THIS session (optimistic; also POSTed).
	const [localCompleted, setLocalCompleted] = useState<ReadonlySet<string>>(new Set());
	// Auto-shown tutorials this session — avoids re-popping after an early close.
	const autoShownRef = useRef<Set<string>>(new Set());

	const isLogged = status === 'authenticated' && Boolean(user);
	// Tours are desktop-only: their spotlight targets and the "Guía" buttons are
	// hidden on phones, so a tour must not auto-launch there either.
	const isMobile = useIsMobile();

	// Completed set derived DIRECTLY from the session user (plus this session's marks).
	// Deriving it here — instead of copying user.tutorialesCompletados into state via
	// an effect — means the auto-trigger sees the real value on the SAME render that
	// /auth/me lands. The old lagging effect let the trigger read an empty [] first
	// and flash the tour for an already-completed tutorial.
	const completados = useMemo(
		() => new Set<string>([...(user?.tutorialesCompletados ?? []), ...localCompleted]),
		[user?.tutorialesCompletados, localCompleted],
	);

	// Auto-show the tour that applies to the current route (home, exportar, editar)
	// for logged-in users who have not completed it yet. Each route shows its tour
	// once on first visit; any exit persists it (see close), so it never re-pops.
	useEffect(() => {
		if (!isLogged) return;
		if (isMobile) return;
		if (active) return;
		const clave = tutorialForPath(pathname);
		if (!clave) return;
		if (completados.has(clave)) return;
		if (autoShownRef.current.has(clave)) return;
		autoShownRef.current.add(clave);
		setActive({ clave, mode: 'auto' });
	}, [isLogged, isMobile, pathname, completados, active]);

	const launch = useCallback(
		(clave: TutorialClave, opts?: LaunchOptions) => setActive({ clave, mode: 'manual', previewName: opts?.previewName }),
		[],
	);

	const persist = useCallback((clave: TutorialClave) => {
		setLocalCompleted((prev) => (prev.has(clave) ? prev : new Set(prev).add(clave)));
		// Best-effort: a failed persist just means it may auto-show again.
		void marcarTutorialCompletado(clave).catch(() => {});
	}, []);

	// Any exit from an AUTO tour (Saltar, X, Esc, or reaching the end) marks it as
	// seen — the user can always relaunch it from the help button, so there is no
	// checkbox and no reason to nag again. Manual tours (help button) never persist.
	const close = useCallback(() => {
		if (active?.mode === 'auto') persist(active.clave);
		setActive(null);
	}, [active, persist]);

	const value = useMemo<TutorialContextValue>(
		() => ({ launch, tutorialForCurrentPath: tutorialForPath(pathname) }),
		[launch, pathname],
	);

	return (
		<TutorialContext.Provider value={value}>
			{children}
			{active ? (
				<SpotlightTour steps={TUTORIAL_STEPS[active.clave]} previewName={active.previewName} onClose={close} />
			) : null}
		</TutorialContext.Provider>
	);
}

export function useTutorial() {
	const ctx = useContext(TutorialContext);
	if (!ctx) throw new Error('useTutorial debe usarse dentro de <TutorialProvider>.');
	return ctx;
}
